import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  Ban,
  Banknote,
  Briefcase,
  Building2,
  CalendarDays,
  Check,
  CheckCheck,
  Frown,
  Handshake,
  HeartHandshake,
  Info,
  Loader2,
  Meh,
  MessageSquareText,
  Pencil,
  Smile,
  Sparkles,
  Truck,
  UserRoundPlus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { formatDateBR } from "@/utils/dateUtils";
import {
  DIMENSION_LABELS,
  DIMENSION_VALUE_LABELS,
  DIMENSION_VALUES,
  NEGOCIACAO_STATUS_LABELS,
  TOM_LABELS,
  type WhatsAppDisponibilidadePayload,
  type WhatsAppItem,
  type WhatsAppItemCategoria,
  type WhatsAppMotivoDescarte,
  type WhatsAppNegociacaoPayload,
  type WhatsAppSentimentoPayload,
} from "@/types/whatsapp";
import { WhatsAppDescartarPopover } from "./WhatsAppDescartarPopover";
import { WhatsAppEvidencia } from "./WhatsAppEvidencia";
import {
  WhatsAppCategoriaBadge,
  WhatsAppConfidenceBadge,
  WhatsAppItemStatusBadge,
} from "./WhatsAppBadges";

const SEM_POSICAO = "__historico__";

/** Itens do mesmo contato (freelancer ou telefone), revisados juntos. */
export interface WhatsAppContatoGroup {
  key: string;
  freelancerId: string | null;
  freelancerNome: string | null;
  contactName: string | null;
  telefone: string | null;
  items: WhatsAppItem[];
}

interface WhatsAppContatoCardProps {
  group: WhatsAppContatoGroup;
  aprovandoId: string | null;
  aprovandoLote: boolean;
  onAprovar: (
    item: WhatsAppItem,
    posicaoId?: string,
    payloadCorrigido?: Record<string, unknown>,
  ) => void;
  onRejeitar: (
    item: WhatsAppItem,
    motivo?: WhatsAppMotivoDescarte,
    nota?: string,
  ) => void;
  onAprovarTodos: (items: WhatsAppItem[]) => void;
  onAssociar: (item: WhatsAppItem) => void;
  onIgnorar: (item: WhatsAppItem) => void;
  onVerConversa: (item: WhatsAppItem) => void;
}

/** contact_name da Speeke vira o próprio número quando não há nome salvo;
 * nesse caso mostrar os dois vira "número · número". */
function contatoDetalhe(contactName: string | null, telefone: string | null): string {
  const digits = (valor: string | null) => (valor ?? "").replace(/\D/g, "");
  const partes: string[] = [];
  if (contactName && digits(contactName) !== digits(telefone)) {
    partes.push(contactName);
  }
  if (telefone) partes.push(telefone);
  return partes.join(" · ");
}

function formatBRL(centavos: number): string {
  return (centavos / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatPeriodo(periodo: string | null): string {
  if (periodo === "mensal") return "/mês";
  if (periodo === "diaria") return "/diária";
  if (periodo === "hora") return "/hora";
  if (periodo === "projeto") return " pelo projeto";
  return "";
}

// Borda lateral por categoria (mesmas cores dos badges).
const CATEGORIA_BORDER: Record<WhatsAppItemCategoria, string> = {
  disponibilidade: "border-l-teal-500",
  negociacao: "border-l-blue-600",
  sentimento: "border-l-pink-600",
};

const DIMENSION_ICONS: Record<string, LucideIcon> = {
  interesse_freela: Handshake,
  situacao_emprego: Briefcase,
  relacao_conosco: HeartHandshake,
};

const TOM_ICONS: Record<string, LucideIcon> = {
  positivo: Smile,
  neutro: Meh,
  negativo: Frown,
  critico: AlertTriangle,
};

interface FatoLinha {
  icon: LucideIcon;
  label: string;
  valor: string | null;
  className?: string;
}

/** Fundo do box do fato conforme o que ele significa. Vermelho e verde são
 * cores de estado, não de categoria: entram só quando há de fato um problema
 * (ou o oposto), e nunca no card inteiro. */
function fatoBoxStyle(item: WhatsAppItem): string {
  if (item.categoria !== "sentimento") return "border-border";
  const payload = item.payload as WhatsAppSentimentoPayload;
  if (payload.conversa_critica || payload.tom === "critico") {
    return "border-red-200 bg-red-50/70";
  }
  if (payload.tom === "negativo") return "border-red-200 bg-red-50/40";
  if (payload.tom === "positivo") return "border-emerald-200 bg-emerald-50/40";
  return "border-border";
}

/** Resumo do fato numa linha só, pro feed de histórico. */
export function fatoResumo(item: WhatsAppItem): string {
  return fatoLinhas(item)
    .filter((linha) => linha.valor)
    .map((linha) => `${linha.label}: ${linha.valor}`)
    .join(" · ");
}

/** Linhas padronizadas do fato extraído: cada categoria sempre exibe o mesmo
 * conjunto base de campos ("não informado" quando faltar), pros cards ficarem
 * uniformes. */
function fatoLinhas(item: WhatsAppItem): FatoLinha[] {
  if (item.categoria === "disponibilidade") {
    const payload = item.payload as WhatsAppDisponibilidadePayload;
    return [
      {
        icon: DIMENSION_ICONS[payload.dimension] ?? Handshake,
        label: DIMENSION_LABELS[payload.dimension] ?? payload.dimension,
        valor: DIMENSION_VALUE_LABELS[payload.value] ?? payload.value,
      },
    ];
  }
  if (item.categoria === "negociacao") {
    const payload = item.payload as WhatsAppNegociacaoPayload;
    const linhas: FatoLinha[] = [
      {
        icon: Handshake,
        label: "Status sugerido",
        valor: payload.status_sugerido
          ? (NEGOCIACAO_STATUS_LABELS[payload.status_sugerido] ??
            payload.status_sugerido)
          : null,
      },
      {
        icon: Banknote,
        label: "Valor",
        valor:
          payload.valor_centavos != null
            ? `${formatBRL(payload.valor_centavos)}${formatPeriodo(payload.periodo)}`
            : null,
      },
      {
        icon: CalendarDays,
        label: "Disponível a partir de",
        valor: payload.data_disponibilidade
          ? formatDateBR(payload.data_disponibilidade)
          : null,
      },
    ];
    if (payload.vaga_sugerida_label) {
      linhas.push({
        icon: Sparkles,
        label: "Vaga sugerida",
        valor: payload.vaga_sugerida_label,
      });
    } else if (payload.vaga_mencionada) {
      linhas.push({
        icon: Briefcase,
        label: "Vaga mencionada",
        valor: payload.vaga_mencionada,
      });
    }
    if (payload.eh_pretensao) {
      linhas.push({
        icon: Info,
        label: "Atenção",
        valor: "Pretensão salarial, não é valor fechado",
        className: "text-amber-700",
      });
    }
    if (payload.contraparte === "agencia") {
      linhas.push({ icon: Building2, label: "Contraparte", valor: "Agência" });
    } else if (payload.contraparte === "fornecedor") {
      linhas.push({ icon: Truck, label: "Contraparte", valor: "Fornecedor" });
    }
    return linhas;
  }
  const payload = item.payload as WhatsAppSentimentoPayload;
  const tomNegativo = payload.tom === "negativo" || payload.tom === "critico";
  const linhas: FatoLinha[] = [
    {
      icon: TOM_ICONS[payload.tom] ?? Meh,
      label: "Tom",
      valor: TOM_LABELS[payload.tom] ?? payload.tom,
      className: tomNegativo
        ? "text-red-700"
        : payload.tom === "positivo"
          ? "text-emerald-700"
          : undefined,
    },
    {
      icon: HeartHandshake,
      label: "Relacionamento",
      valor: payload.facilidade_relacionamento
        ? payload.facilidade_relacionamento.charAt(0).toUpperCase() +
          payload.facilidade_relacionamento.slice(1)
        : null,
    },
    {
      icon: Info,
      label: "Motivo de insatisfação",
      valor: payload.motivo_insatisfacao,
      className: payload.motivo_insatisfacao ? "text-red-700" : undefined,
    },
  ];
  if (payload.conversa_critica) {
    linhas.push({
      icon: AlertTriangle,
      label: "Atenção",
      valor: "Conversa crítica",
      className: "text-red-700 font-medium",
    });
  }
  return linhas;
}

/** Fatos que a IA tirou do mesmo trecho: mesma conversa, mesmo dia, mesmo
 * resumo. Três eixos de disponibilidade viravam três cards repetindo a mesma
 * frase; aqui o resumo aparece uma vez e os fatos ficam embaixo. */
interface BlocoDeFatos {
  key: string;
  resumo: string | null;
  items: WhatsAppItem[];
}

function agruparPorTrecho(items: WhatsAppItem[]): BlocoDeFatos[] {
  const blocos: BlocoDeFatos[] = [];
  for (const item of items) {
    const dia = item.observed_at.slice(0, 10);
    const key = `${item.conversation_id}|${dia}|${item.resumo ?? ""}`;
    const bloco = blocos.find((candidato) => candidato.key === key);
    if (bloco) {
      bloco.items.push(item);
    } else {
      blocos.push({ key, resumo: item.resumo || null, items: [item] });
    }
  }
  return blocos;
}

interface PosicaoKanbanRow {
  id: string;
  vaga_id: string;
}

// vagas_kanban_posicoes não está nos types gerados do Supabase; cast local
// até regenerar (mesma situação do kanban-api.ts).
const supabaseSemTipos = supabase as unknown as {
  from(table: string): {
    select(colunas: string): {
      eq(
        coluna: string,
        valor: string,
      ): {
        limit(maximo: number): Promise<{
          data: PosicaoKanbanRow[] | null;
          error: { message: string } | null;
        }>;
      };
    };
  };
};

/** Posições de kanban do freelancer, pra vincular a negociação aprovada. */
function usePosicoesDoFreelancer(freelancerId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["whatsapp-posicoes-freelancer", freelancerId],
    queryFn: async () => {
      const { data: posicoes, error } = await supabaseSemTipos
        .from("vagas_kanban_posicoes")
        .select("id, vaga_id")
        .eq("freelancer_id", freelancerId!)
        .limit(50);
      if (error) throw new Error(error.message);
      if (!posicoes?.length) return [];
      const vagaIds = [...new Set(posicoes.map((posicao) => posicao.vaga_id))];
      const { data: vagas, error: vagasError } = await supabase
        .from("vagas")
        .select("id, agencia, cliente, status")
        .in("id", vagaIds);
      if (vagasError) throw vagasError;
      const vagaById = new Map((vagas ?? []).map((vaga) => [vaga.id, vaga]));
      return posicoes.map((posicao) => {
        const vaga = vagaById.get(posicao.vaga_id);
        return {
          id: posicao.id,
          vagaId: posicao.vaga_id,
          label: vaga
            ? [vaga.agencia, vaga.cliente].filter(Boolean).join(" · ")
            : posicao.vaga_id.slice(0, 8),
        };
      });
    },
    enabled: enabled && !!freelancerId,
    staleTime: 5 * 60 * 1000,
  });
}

function ItemRow({
  item,
  freelancerId,
  aprovando,
  mostrarResumo,
  onAprovar,
  onRejeitar,
  onVerConversa,
}: {
  item: WhatsAppItem;
  freelancerId: string | null;
  aprovando: boolean;
  mostrarResumo: boolean;
  onAprovar: (
    item: WhatsAppItem,
    posicaoId?: string,
    payloadCorrigido?: Record<string, unknown>,
  ) => void;
  onRejeitar: (
    item: WhatsAppItem,
    motivo?: WhatsAppMotivoDescarte,
    nota?: string,
  ) => void;
  onVerConversa: (item: WhatsAppItem) => void;
}) {
  const [posicaoId, setPosicaoId] = useState<string>(SEM_POSICAO);
  // Valor ajustado pelo revisor antes de aprovar: vale mais que o da IA e
  // ensina a direção do erro.
  const [valorCorrigido, setValorCorrigido] = useState<string | null>(null);
  const [usarSugestao, setUsarSugestao] = useState(true);
  const pendente = item.review_status === "pendente";
  const precisaPosicao = item.categoria === "negociacao" && pendente;
  const posicoesQuery = usePosicoesDoFreelancer(freelancerId, precisaPosicao);

  const vagaSugeridaId =
    item.categoria === "negociacao"
      ? ((item.payload as WhatsAppNegociacaoPayload).vaga_sugerida_id ?? null)
      : null;
  const posicaoSugerida =
    (vagaSugeridaId &&
      posicoesQuery.data?.find((posicao) => posicao.vagaId === vagaSugeridaId)) ||
    null;
  const sugestaoAtiva = usarSugestao && !!posicaoSugerida;
  const posicaoEfetiva = sugestaoAtiva
    ? posicaoSugerida!.id
    : posicaoId === SEM_POSICAO
      ? undefined
      : posicaoId;

  return (
    <div
      className={cn(
        "space-y-2.5 rounded-md border border-l-4 p-3",
        CATEGORIA_BORDER[item.categoria],
        !pendente && "opacity-70",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <WhatsAppCategoriaBadge categoria={item.categoria} />
        <div className="flex shrink-0 items-center gap-2">
          <WhatsAppConfidenceBadge confidence={item.confidence} />
          {!pendente && <WhatsAppItemStatusBadge status={item.review_status} />}
          <span className="text-xs text-muted-foreground">
            {formatDateBR(item.observed_at)}
          </span>
        </div>
      </div>

      {mostrarResumo && item.resumo && (
        <p className="flex items-start gap-1.5 text-sm font-medium">
          <Sparkles
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70"
            aria-label="Resumo extraído por IA"
          />
          {item.resumo}
        </p>
      )}

      {/* O fato extraído e a decisão sobre ele, lado a lado enquanto couber */}
      <div
        className={cn(
          "flex flex-wrap items-end justify-between gap-x-4 gap-y-2 rounded-md border p-3",
          fatoBoxStyle(item),
        )}
      >
        <div className="min-w-0 space-y-1">
          {pendente && item.categoria === "disponibilidade" && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">
                {DIMENSION_LABELS[
                  (item.payload as WhatsAppDisponibilidadePayload).dimension
                ] ?? "Valor"}
                :
              </span>
              <Select
                value={
                  valorCorrigido ??
                  (item.payload as WhatsAppDisponibilidadePayload).value
                }
                onValueChange={(valor) =>
                  setValorCorrigido(
                    valor === (item.payload as WhatsAppDisponibilidadePayload).value
                      ? null
                      : valor,
                  )
                }
              >
                <SelectTrigger className="h-8 w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(
                    DIMENSION_VALUES[
                      (item.payload as WhatsAppDisponibilidadePayload).dimension
                    ] ?? []
                  ).map((valor) => (
                    <SelectItem key={valor} value={valor}>
                      {DIMENSION_VALUE_LABELS[valor] ?? valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {valorCorrigido && (
                <span className="text-xs text-amber-700">
                  a IA disse{" "}
                  {DIMENSION_VALUE_LABELS[
                    (item.payload as WhatsAppDisponibilidadePayload).value
                  ] ?? (item.payload as WhatsAppDisponibilidadePayload).value}
                </span>
              )}
            </div>
          )}
          {(pendente && item.categoria === "disponibilidade"
            ? []
            : fatoLinhas(item)
          ).map((linha) => {
            const LinhaIcon = linha.icon;
            return (
              <div key={linha.label} className="flex items-center gap-2 text-sm">
                <LinhaIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{linha.label}:</span>
                {linha.valor ? (
                  <span className={cn("font-medium", linha.className)}>
                    {linha.valor}
                  </span>
                ) : (
                  <span className="text-muted-foreground/60">não informado</span>
                )}
              </div>
            );
          })}
        </div>

        {pendente && (
          <div className="flex flex-wrap items-center gap-2">
            {sugestaoAtiva && (
              <div className="flex items-center gap-1.5 text-sm">
                <Sparkles className="h-3.5 w-3.5 text-primary/70" />
                <span>
                  Vincular à vaga sugerida:{" "}
                  <span className="font-medium">{posicaoSugerida!.label}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-muted-foreground"
                  onClick={() => setUsarSugestao(false)}
                >
                  Mudar
                </Button>
              </div>
            )}
            {!sugestaoAtiva && precisaPosicao && (posicoesQuery.data?.length ?? 0) > 0 && (
              <Select value={posicaoId} onValueChange={setPosicaoId}>
                <SelectTrigger className="h-8 w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_POSICAO}>
                    Só registrar como histórico
                  </SelectItem>
                  {posicoesQuery.data!.map((posicao) => (
                    <SelectItem key={posicao.id} value={posicao.id}>
                      Vincular: {posicao.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <WhatsAppDescartarPopover
              item={item}
              descartando={aprovando}
              onDescartar={onRejeitar}
            />
            <Button
              size="sm"
              className="h-8 gap-1.5"
              onClick={() =>
                onAprovar(
                  item,
                  posicaoEfetiva,
                  valorCorrigido ? { value: valorCorrigido } : undefined,
                )
              }
              disabled={aprovando}
            >
              {aprovando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Aprovar
            </Button>
          </div>
        )}
      </div>

      {(item.quotes.length > 0 || item.message_ids.length > 0) && (
        <WhatsAppEvidencia item={item} onVerConversaCompleta={onVerConversa} />
      )}
    </div>
  );
}

export function WhatsAppContatoCard({
  group,
  aprovandoId,
  aprovandoLote,
  onAprovar,
  onRejeitar,
  onAprovarTodos,
  onAssociar,
  onIgnorar,
  onVerConversa,
}: WhatsAppContatoCardProps) {
  const pendentes = group.items.filter(
    (item) => item.review_status === "pendente",
  );
  // Sem posição selecionável no lote: negociação aprovada em lote vira só
  // histórico; disponibilidade exige freelancer associado.
  const aprovaveisEmLote = pendentes.filter(
    (item) => item.categoria === "sentimento" || group.freelancerId,
  );
  const primeiroPendente = pendentes[0] ?? group.items[0];

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {group.freelancerId ? (
              <Link
                to={`/freelancers/${group.freelancerId}`}
                className="text-base font-semibold text-primary hover:underline"
              >
                {group.freelancerNome ?? "Ver freelancer"}
              </Link>
            ) : (
              <>
                <span className="text-base font-semibold">
                  {group.contactName ?? group.telefone ?? "Contato sem nome"}
                </span>
                <span className="rounded bg-amber-50 px-2 py-0.5 text-sm text-amber-800">
                  sem freelancer associado
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5"
                  onClick={() => onAssociar(primeiroPendente)}
                >
                  <UserRoundPlus className="h-3.5 w-3.5" />
                  Associar
                </Button>
              </>
            )}
            {group.freelancerId &&
              contatoDetalhe(group.contactName, group.telefone) && (
                <span className="text-sm text-muted-foreground">
                  {contatoDetalhe(group.contactName, group.telefone)}
                </span>
              )}
          </div>
          <div className="flex items-center gap-2">
            {pendentes.length > 0 && (
              <span className="text-sm font-medium text-primary">
                {pendentes.length} pendente{pendentes.length > 1 ? "s" : ""}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => onVerConversa(primeiroPendente)}
            >
              <MessageSquareText className="h-4 w-4" />
              Ver conversa
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-muted-foreground"
              title="Parar de capturar e processar esse contato (fornecedor etc.)"
              onClick={() => onIgnorar(primeiroPendente)}
            >
              <Ban className="h-4 w-4" />
              Ignorar contato
            </Button>
            {aprovaveisEmLote.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => onAprovarTodos(aprovaveisEmLote)}
                disabled={aprovandoLote}
              >
                {aprovandoLote ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCheck className="h-4 w-4" />
                )}
                Aprovar todos ({aprovaveisEmLote.length})
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {agruparPorTrecho(group.items).map((bloco) => (
            <div key={bloco.key} className="space-y-2">
              {bloco.items.length > 1 && bloco.resumo && (
                <p className="flex items-start gap-1.5 text-sm font-medium">
                  <Sparkles
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70"
                    aria-label="Resumo extraído por IA"
                  />
                  {bloco.resumo}
                </p>
              )}
              {bloco.items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  freelancerId={group.freelancerId}
                  aprovando={aprovandoId === item.id}
                  mostrarResumo={bloco.items.length === 1}
                  onAprovar={onAprovar}
                  onRejeitar={onRejeitar}
                  onVerConversa={onVerConversa}
                />
              ))}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
